/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import bcrypt from "bcryptjs";
import cn from "classnames";
import { SubmitHandler, useForm } from "react-hook-form";

import { adminLogin } from "lib/v3/loginAPI";
import { useAuth } from "state";

import HidePasswordIcon from "components/icons/HidePasswordIcon";
import ShowPasswordIcon from "components/icons/ShowPasswordIcon";
import Stack from "components/ui/Stack";
import Text from "components/ui/Text";
import Label from "components/ui/Label";
import { Input } from "components/ui/Input";
import { Button } from "components/ui/Button";
import { LoginResponse } from "pages/Login/types/login.types";

import styles from "../../style.module.css";

import { useEnvs } from "state/EnvironmentsContext/EnvContext";

interface IFormInput {
  username: string;
  password: string;
}

const LoginForm = ({
  setCurrentForm,
  loginDataRef,
  setUsername,
}: {
  setCurrentForm: (formName: string) => void;
  loginDataRef: React.MutableRefObject<LoginResponse | null>;
  setUsername: (username: string) => void;
}) => {
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const { meta_title } = useEnvs();
  const { isLoading: isAuthLoading } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const PASSWORD_SALT = useEnvs((state) => state.password_salt);

  const loginMutation = useMutation(
    ({ username, password }: IFormInput) => {
      const hashPassword = bcrypt.hashSync(password, PASSWORD_SALT);

      return adminLogin(username.trim(), hashPassword);
    },
    {
      onSuccess: (res) => {
        loginDataRef.current = res.data;
        setUsername(watch("username"));
        setCurrentForm(res.data.step);
      },
      onError: () => {
        setError("password", {
          type: "manual",
          message: "Invalid login information, please check and try again.",
        });
      },
    },
  );

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    loginMutation.mutate(data);
  };

  const username = watch("username");
  const password = watch("password");
  const isButtonDisabled = !username || !password;

  useEffect(() => {
    if (password) {
      clearErrors("password");
    }
  }, [password, clearErrors]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Stack className="space-y-1 mb-8">
          <h3 className="text-white text-xl font-bold">Login</h3>
          <Text className="text-base text-[#9A9A9A]">
            Welcome to {meta_title}, Enter your credentials to continue.
          </Text>
        </Stack>

        <Stack className="mb-5">
          <Label
            htmlFor="username"
            isRequired
            isBold={false}
            className="font-bold text-base text-c-gray-200 mb-2"
          >
            Username
          </Label>

          <Input
            id="username"
            placeholder="john.doe@org.com"
            {...register("username", {
              required: "Username is required",
            })}
          />
        </Stack>

        <Stack className="mb-6">
          <Label
            htmlFor="password"
            isRequired
            isBold={false}
            className="font-bold text-base text-c-gray-200 mb-2"
          >
            Password
          </Label>

          <div className="relative">
            <Input
              type={isPasswordShown ? "text" : "password"}
              id="password"
              placeholder="**************"
              {...register("password", {
                required: "Password is required",
              })}
            />

            <button
              type="button"
              className="absolute flex right-3 items-center bg-secondary-accent h-fit top-2/4 transform -translate-y-2/4 rounded-full p-1"
              onClick={() => setIsPasswordShown(!isPasswordShown)}
            >
              {isPasswordShown ? <HidePasswordIcon /> : <ShowPasswordIcon />}
            </button>
          </div>
        </Stack>

        {errors.password && (
          <Text className="mb-5 text-xs text-[#F55B56]">
            {errors.password.message}
          </Text>
        )}

        <Button
          variant="primary"
          type="submit"
          size="xl"
          isLoading={loginMutation.isLoading || isAuthLoading}
          id="loginBtn"
          shouldPreventDefault={false}
          disabled={
            isButtonDisabled || loginMutation.isLoading || isAuthLoading
          }
          className={cn("mb-5", {
            [styles.disabled]:
              isButtonDisabled || loginMutation.isLoading || isAuthLoading,
          })}
        >
          Login
        </Button>

        {/* Ahmed Amin request plz don't change it back */}

        {/* 
        <Button
          variant="unstyled"
          className="text-white text-xs underline font-bold"
          onClick={() => navigate("/forget-password")}
        >
          Forgot Your Password?
        </Button> */}
      </Stack>
    </form>
  );
};

export default LoginForm;
